/**
 * Predictive search (Shopify native) — restricted to blog articles.
 *
 * - Attaches to forms tagged with [data-blog-search-form].
 * - Predictive dropdown calls /search/suggest.json?resources[type]=article.
 * - Submitting the form redirects to the blog URL with ?q=...
 * - On the blog page, when ?q is in the URL, the default article grid is
 *   hidden and a filtered grid is rendered using the same .article-item
 *   markup so spacing/cards stay consistent.
 *
 * Docs: https://shopify.dev/docs/api/ajax/reference/predictive-search
 */
(function () {
  const DEBOUNCE_MS = 220;
  const MIN_QUERY_LENGTH = 2;
  const PAGE_LIMIT = 50;

  function debounce(fn, wait) {
    let timer;
    return function debounced(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function escapeHtml(value) {
    if (value == null) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    try {
      return date.toLocaleDateString(document.documentElement.lang || 'fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch (_) {
      return date.toISOString().slice(0, 10);
    }
  }

  function stripHtml(value) {
    if (!value) return '';
    return value.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  }

  // ---- Predictive dropdown items -------------------------------------------

  function buildDropdownItem(article) {
    const url = escapeHtml(article.url);
    const title = escapeHtml(article.title);
    const image = article.image ? escapeHtml(article.image) : '';
    const date = formatDate(article.published_at);
    const summary = article.summary_html
      ? escapeHtml(stripHtml(article.summary_html).slice(0, 120))
      : '';

    return `
      <a href="${url}" class="blog-search__result-item" role="option">
        ${
          image
            ? `<span class="blog-search__result-image-wrapper"><img src="${image}" alt="" loading="lazy" class="blog-search__result-image"></span>`
            : '<span class="blog-search__result-image-wrapper blog-search__result-image-wrapper--empty" aria-hidden="true"></span>'
        }
        <span class="blog-search__result-info">
          <span class="blog-search__result-title">${title}</span>
          ${date ? `<span class="blog-search__result-date">${date}</span>` : ''}
          ${summary ? `<span class="blog-search__result-summary">${summary}</span>` : ''}
        </span>
      </a>
    `;
  }

  function renderEmptyState(query) {
    return `
      <p class="blog-search__no-result">
        Aucun article ne correspond à « ${escapeHtml(query)} ».
      </p>
    `;
  }

  function renderViewAll(query, blogUrl) {
    const params = new URLSearchParams({ q: query });
    return `
      <a href="${blogUrl}?${params.toString()}" class="blog-search__view-all">
        Voir tous les résultats pour « ${escapeHtml(query)} »
      </a>
    `;
  }

  // ---- Full-grid items (mirrors snippets/article-item.liquid layout) -------

  function buildGridItem(article) {
    const url = escapeHtml(article.url);
    const title = escapeHtml(article.title);
    const image = article.image ? escapeHtml(article.image) : '';
    const date = formatDate(article.published_at);
    const summary = article.summary_html
      ? escapeHtml(stripHtml(article.summary_html).slice(0, 200))
      : '';

    return `
      <div class="block-list__item 1/1 1/2--tablet 1/3--desk">
        <div class="article-item">
          ${
            image
              ? `<a href="${url}" class="article-item__image-container">
                  <img src="${image}" alt="${title}" loading="lazy" class="article-item__image">
                </a>`
              : ''
          }
          <h2 class="article-item__title">
            <a href="${url}" class="link">${title}</a>
          </h2>
          ${
            summary
              ? `<div class="article-item__excerpt rte"><p>${summary}</p></div>`
              : ''
          }
          ${
            date
              ? `<div class="article-item__meta"><time class="article-item__meta-item">${date}</time></div>`
              : ''
          }
        </div>
      </div>
    `;
  }

  // ---- Predictive dropdown init --------------------------------------------

  function initBlogSearch(form) {
    const input = form.querySelector('[data-blog-search-input]');
    const hiddenQ = form.querySelector('[data-blog-search-q]');
    const clearBtn = form.querySelector('[data-blog-search-clear]');
    const resultsWrap = form.querySelector('[data-blog-search-results]');
    const resultsInner = form.querySelector('[data-blog-search-results-inner]');
    const predictiveUrl = form.dataset.predictiveUrl || '/search/suggest';
    const blogUrl = form.dataset.blogUrl || form.getAttribute('action') || '/blogs/news';
    const limit = Math.max(1, Math.min(10, parseInt(form.dataset.limit, 10) || 6));

    if (!input || !resultsInner) return;

    let currentController = null;

    function openResults() {
      resultsWrap.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      form.classList.add('blog-search__form--open');
    }

    function closeResults() {
      resultsWrap.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      form.classList.remove('blog-search__form--open');
    }

    function setLoading(isLoading) {
      form.classList.toggle('blog-search__form--loading', isLoading);
    }

    function clearAll() {
      input.value = '';
      if (hiddenQ) hiddenQ.value = '';
      resultsInner.innerHTML = '';
      closeResults();
      clearBtn.hidden = true;
      input.focus();
    }

    function renderResults(articles, query) {
      if (!articles.length) {
        resultsInner.innerHTML = renderEmptyState(query);
      } else {
        const items = articles.map(buildDropdownItem).join('');
        resultsInner.innerHTML = `
          <div class="blog-search__result-list">${items}</div>
          ${renderViewAll(query, blogUrl)}
        `;
      }
      openResults();
    }

    async function fetchResults(query) {
      if (currentController) currentController.abort();
      currentController = new AbortController();

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'article',
        'resources[limit]': String(limit),
        'resources[options][unavailable_products]': 'last',
      });

      const url = `${predictiveUrl}.json?${params.toString()}`;
      setLoading(true);

      try {
        const response = await fetch(url, {
          signal: currentController.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        const articles =
          (data.resources && data.resources.results && data.resources.results.articles) || [];
        renderResults(articles, query);
      } catch (error) {
        if (error.name === 'AbortError') return;
        resultsInner.innerHTML = renderEmptyState(query);
        openResults();
      } finally {
        setLoading(false);
      }
    }

    const onInput = debounce(() => {
      const query = input.value.trim();
      if (hiddenQ) hiddenQ.value = query;
      clearBtn.hidden = query.length === 0;

      if (query.length < MIN_QUERY_LENGTH) {
        resultsInner.innerHTML = '';
        closeResults();
        return;
      }
      fetchResults(query);
    }, DEBOUNCE_MS);

    form.addEventListener('submit', () => {
      if (hiddenQ) hiddenQ.value = input.value.trim();
    });

    input.addEventListener('input', onInput);

    input.addEventListener('focus', () => {
      if (input.value.trim().length >= MIN_QUERY_LENGTH && resultsInner.innerHTML !== '') {
        openResults();
      }
    });

    clearBtn.addEventListener('click', clearAll);

    document.addEventListener('click', (event) => {
      if (!form.contains(event.target)) closeResults();
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeResults();
    });
  }

  // ---- On-blog-page filtering (when ?q= is present) ------------------------

  async function applyBlogPageFilter() {
    const params = new URLSearchParams(window.location.search);
    const query = (params.get('q') || '').trim();
    if (!query) return;

    const filteredWrap = document.querySelector('[data-blog-search-page-results]');
    const filteredGrid = document.querySelector('[data-blog-search-page-grid]');
    const filteredTitle = document.querySelector('[data-blog-search-page-title]');
    const defaultList = document.querySelector('[data-blog-default-list]');
    const input = document.querySelector('[data-blog-search-input]');
    const hiddenQ = document.querySelector('[data-blog-search-q]');

    if (!filteredWrap || !filteredGrid || !defaultList) return;

    if (input) input.value = query;
    if (hiddenQ) hiddenQ.value = query;

    defaultList.hidden = true;
    filteredWrap.hidden = false;
    filteredGrid.innerHTML = `<p class="blog-search-results__loading">Recherche en cours…</p>`;

    try {
      const url = `/search/suggest.json?${new URLSearchParams({
        q: query,
        'resources[type]': 'article',
        'resources[limit]': String(PAGE_LIMIT),
      }).toString()}`;

      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const articles =
        (data.resources && data.resources.results && data.resources.results.articles) || [];

      if (filteredTitle) {
        filteredTitle.textContent = articles.length
          ? `${articles.length} article${articles.length > 1 ? 's' : ''} pour « ${query} »`
          : `Aucun article pour « ${query} »`;
      }

      if (!articles.length) {
        filteredGrid.innerHTML = `<p class="blog-search-results__empty">Essayez un autre mot-clé.</p>`;
        return;
      }

      filteredGrid.innerHTML = articles.map(buildGridItem).join('');
    } catch (_) {
      filteredGrid.innerHTML = `<p class="blog-search-results__empty">Une erreur est survenue. Réessayez.</p>`;
    }
  }

  function init() {
    document.querySelectorAll('[data-blog-search-form]').forEach(initBlogSearch);
    applyBlogPageFilter();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
